/* eslint-disable react/jsx-indent */
import React, { memo, useRef, useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { IoMdDownload } from 'react-icons/io';
import { Button, Modal, Progress } from 'rsuite';
import attachment from '../../../../../../../assets/icons/attachment.svg';
import icon_error_info from '../../../../../../../assets/icons/error.svg';
import icon_alert_circle from '../../../../../../../assets/icons/icon_alert_circle.svg';
import { ButtonPrimary } from '../../../../../../../components';
import { ModalUploadProductProps, UploadState } from '../../../SetupInfoPanelAnaliseDeCobertura/@types';
import { useSetupAderenciaDePrecosStore } from '../../SetupAderenciaDePrecos.store';
import { MODAL_UPLOAD_CONTENT } from './ModalUploadAderenciaSetup.data';
import styles from './ModalUploadAderenciaSetup.module.scss';

export const ModalUploadAderenciaSetup = memo(
  ({
    show,
    type,
    invalidItems,
    uploadProgress,
    onFileSelect,
    onHide,
    onClickCloseAndReset,
    onClickDownloadErrors,
    onClickDownloadModel,
  }: ModalUploadProductProps) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const { uploadState, uploadFileErrorMessage, setSetupAnaliseDeAderenciaState } = useSetupAderenciaDePrecosStore();

    const resetStates = () => {
      setSelectedFile(null);
      setSetupAnaliseDeAderenciaState('uploadState', UploadState.IDLE);
    };

    const handleHideClose = (buttonType: 'fechar' | 'fecharProsseguir') => {
      resetStates();
      if (buttonType === 'fechar') {
        onClickCloseAndReset?.(); // fechar o modal, resetar o estado e tabela
      } else {
        onHide?.(); // fechar e prosseguir a modal, resetar o estado e manter os dados da tabela
      }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file) {
        if (file.type !== 'text/csv') {
          setSetupAnaliseDeAderenciaState('uploadState', UploadState.ERROR);
        }
        onFileSelect?.(file);
        setSelectedFile(file);
      }
    };

    const handleUploadClick = () => {
      fileInputRef.current?.click();
    };

    const handleCancelUpload = () => {
      resetStates();
    };

    const downloadInfoComponent = (
      <div className={styles.fileName}>
        <div className={styles.fileNameContent}>
          <img src={attachment} alt="Attachment" className={styles.fileIcon} />
          <span className={styles.fileNameText} title={selectedFile?.name}>
            {selectedFile?.name}
          </span>
        </div>
        <span className={styles.fileSize}>{Math.round(selectedFile?.size / 1024)}KB</span>
      </div>
    );

    const renderContent = () => {
      if (uploadState === UploadState.PARCIAL) {
        return (
          <div className={styles.successState}>
            {selectedFile && downloadInfoComponent}
            <div className={styles.checkmark}>
              <img src={icon_error_info} alt="Erro" />
              <h4>{MODAL_UPLOAD_CONTENT[type]?.parcial_error_title}</h4>
              <p style={{ marginTop: '12px' }}>{MODAL_UPLOAD_CONTENT[type]?.parcial_error_description}</p>
            </div>
          </div>
        );
      }
      if (uploadState === UploadState.ERROR) {
        return (
          <div className={styles.successState}>
            {selectedFile && downloadInfoComponent}
            <div className={styles.checkmark}>
              <img src={icon_error_info} alt="Erro" />
              <h4>{MODAL_UPLOAD_CONTENT[type]?.full_error_title}</h4>
              <p style={{ marginTop: '12px' }}>{MODAL_UPLOAD_CONTENT[type]?.full_error_description}</p>
            </div>
          </div>
        );
      }
      if (uploadState === UploadState.WRONG_FILE) {
        //erro 400
        return (
          <div className={styles.successState}>
            {selectedFile && downloadInfoComponent}
            <div className={styles.checkmark}>
              <img src={icon_error_info} alt="Erro" />
              <h4>{MODAL_UPLOAD_CONTENT[type]?.wrong_file_title}</h4>
              <p style={{ marginTop: '12px' }}>{MODAL_UPLOAD_CONTENT[type]?.parcial_error_description}</p>
              <p className={styles['error-message']} style={{ marginTop: '12px' }}>
                {uploadFileErrorMessage}
              </p>
            </div>
          </div>
        );
      }
      if (uploadState === UploadState.EXCEED) {
        return (
          <div className={styles.successState}>
            {selectedFile && downloadInfoComponent}
            <div className={styles.checkmark}>
              <img src={icon_alert_circle} alt="alerta-limite" />
              <h4>{MODAL_UPLOAD_CONTENT[type]?.exceed_limit_title}</h4>
              <p style={{ marginTop: '12px' }}>{MODAL_UPLOAD_CONTENT[type]?.exceed_limit_description}</p>
            </div>
          </div>
        );
      }
      return (
        <>
          {selectedFile && uploadState === UploadState.UPLOADING && (
            <div className={styles.successState}>
              {downloadInfoComponent}
              <Progress.Circle percent={uploadProgress} strokeWidth={4} width={70} height={70} />
              <div>
                <span className={styles.uploadMessage}>
                  O arquivo está sendo enviado e o tempo de espera dependerá do tamanho do arquivo. <strong>Por favor, aguarde...</strong>
                </span>
                <span>
                  <Button appearance="link" className={styles.cancelButton} onClick={handleCancelUpload}>
                    Cancelar envio
                  </Button>
                </span>
              </div>
            </div>
          )}

          <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} />

          {uploadState !== UploadState.UPLOADING && (
            <Button appearance="primary" className={styles.uploadButton} onClick={handleUploadClick}>
              {MODAL_UPLOAD_CONTENT[type]?.button}
            </Button>
          )}
        </>
      );
    };

    return (
      <Modal className={styles.modal} show={show} onHide={() => handleHideClose('fechar')}>
        <Modal.Header>
          <div className={styles.title}>
            <h3 className={styles.modalTitle}>{MODAL_UPLOAD_CONTENT[type]?.title}</h3>
          </div>
        </Modal.Header>
        <div className={styles.content}>
          <p className={styles.description}>{MODAL_UPLOAD_CONTENT[type]?.subtitle}</p>
          {renderContent()}
        </div>
        {uploadState === UploadState.IDLE && (
          <div className={styles.infoBox}>
            <p className={styles.infoBoxText}>Baixe o modelo, preencha e reenvie o mesmo modelo, sem alterar as informações que já estão no documento.</p>
            <Button appearance="link" className={styles.infoBoxLink} onClick={onClickDownloadModel}>
              Baixar modelo <FaExternalLinkAlt />
            </Button>
          </div>
        )}

        {uploadState === UploadState.PARCIAL || uploadState === UploadState.ERROR ? (
          <div className={styles.buttonsContainer}>
            <ButtonPrimary className={styles.downloadButton} type="button" onClick={onClickDownloadErrors}>
              {MODAL_UPLOAD_CONTENT[type]?.parcial_error_download_button}
              <IoMdDownload />
            </ButtonPrimary>
          </div>
        ) : null}
        {uploadState === UploadState.EXCEED ? (
          <div className={styles.buttonsContainer}>
            <Button className={styles.closeErrorButton} type="button" appearance="subtle" onClick={resetStates}>
              Reenviar arquivo
            </Button>
          </div>
        ) : null}
        {uploadState === UploadState.UPLOADING || uploadState === UploadState.IDLE || uploadState === UploadState.WRONG_FILE ? (
          <Modal.Footer className={styles.footer}>
            <Button onClick={() => handleHideClose('fechar')} appearance="subtle" className={styles.closeButton}>
              Fechar
            </Button>
          </Modal.Footer>
        ) : null}
      </Modal>
    );
  },
);
